import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { DepartmentSizeQueryFilter } from "@/types/campaign";
import { pluralize } from "@/utils/string-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DialogTrigger, Button as RACButton } from "react-aria-components";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import z from "zod";
import { Button } from "../generic/Button";
import { IconCircle } from "../generic/IconCircle";
import { Modal } from "../generic/Modal";
import { NumberField } from "../generic/NumberField";
import { Select, SelectItem } from "../generic/Select";

const departments = [
  "Administration",
  "Advokater",
  "Forretningsudvikling",
  "IT",
  "IT Udvikling",
  "Jurister",
  "Kommunikation",
  "Konsulenter",
  "Kundeservice",
  "Kvalitet",
  "Logistik og Supply Chain",
  "Marketing",
  "Økonomi",
  "Partnere",
  "Personale",
  "Rådgivere",
  "Redaktion",
  "Revisor",
  "Salg",
  "Sekretariat",
  "Service",
  "Undervisere",
];

const departmentFormSchema = z
  .object({
    department: z
      .string("Afdeling er påkrævet")
      .nonempty("Afdeling er påkrævet")
      .nonoptional("Afdeling er påkrævet"),
    minSize: z.number().nullable(),
    maxSize: z.number().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.minSize === null && data.maxSize === null) {
      ctx.addIssue({
        code: "invalid_value",
        path: ["minSize"],
        values: [data.minSize],
        message: "Min. størrelse eller Maks. størrelse skal udfyldes",
      });
      ctx.addIssue({
        code: "invalid_value",
        path: ["maxSize"],
        values: [data.maxSize],
        message: "Min. størrelse eller Maks. størrelse skal udfyldes",
      });
      return;
    }
    if (
      data.minSize !== null &&
      data.maxSize !== null &&
      data.minSize > data.maxSize
    ) {
      ctx.addIssue({
        code: "invalid_value",
        path: ["minSize"],
        values: [data.minSize],
        message: "Min. størrelse skal være mindre end Maks. størrelse",
      });
    }
  });

const departmentQueryFilterSchema = z.object({
  departments: z.array(departmentFormSchema),
});

export function DepartmentSizeFilter({
  group: { title, description, filters },
  formId,
  queryFilters,
  update,
  setShowButtons,
  setIsDirty,
}: FilterGroupComponentProps<"value", object>) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const convertQueryFiltersToFormValues = () => {
    const departments: z.infer<typeof departmentFormSchema>[] = [];
    if (queryFilters.value?.operator === "DepartmentSize") {
      for (const group of queryFilters.value.groups) {
        const minSize =
          group.operator === "GreaterEqual" ||
          group.operator === "Between" ||
          group.operator === "Equal"
            ? parseInt(group.values[1], 10)
            : null;
        const maxSize =
          group.operator === "LessEqual" ||
          group.operator === "Between" ||
          group.operator === "Equal"
            ? group.operator === "Between"
              ? parseInt(group.values[2], 10)
              : parseInt(group.values[1], 10)
            : null;
        departments.push({
          department: group.values[0], // Department name is not stored in the query filter
          minSize,
          maxSize,
        });
      }
    }
    return departments;
  };

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(departmentQueryFilterSchema),
    defaultValues: {
      departments: convertQueryFiltersToFormValues(),
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);
  const {
    fields,
    append,
    remove,
    update: updateField,
  } = useFieldArray({
    control,
    name: "departments",
  });

  const hasFields = useMemo(() => fields.length > 0, [fields]);

  useEffect(() => setShowButtons(hasFields), [hasFields, setShowButtons]);

  // Get initial values for the modal based on editing state
  const modalInitialValues = useMemo(() => {
    if (editingIndex !== null && fields[editingIndex]) {
      return fields[editingIndex];
    }
    return { department: "", minSize: null, maxSize: null };
  }, [editingIndex, fields]);

  const departmentsQueryFilter = useMemo<DepartmentSizeQueryFilter>(() => {
    const groups: DepartmentSizeQueryFilter["groups"] = fields.map((dept) => {
      if (dept.minSize !== null && dept.maxSize !== null) {
        if (dept.maxSize === dept.minSize) {
          return {
            operator: "Equal",
            values: [dept.department, dept.minSize.toString()],
          };
        }
        return {
          operator: "Between",
          values: [
            dept.department,
            dept.minSize.toString(),
            dept.maxSize.toString(),
          ],
        };
      } else if (dept.minSize !== null) {
        return {
          operator: "GreaterEqual",
          values: [dept.department, dept.minSize.toString()],
        };
      } else if (dept.maxSize !== null) {
        return {
          operator: "LessEqual",
          values: [dept.department, dept.maxSize.toString()],
        };
      }
      throw new Error(
        "Invalid department size filter: both minSize and maxSize cannot be null",
      );
    });

    return {
      filterName: "basic-department-size",
      fieldName: "departmentsize",
      operator: "DepartmentSize",
      groups,
    };
  }, [fields]);

  const onSubmit = handleSubmit(() => {
    console.debug("Department Size Filter Submit", departmentsQueryFilter);
    update({
      value: departmentsQueryFilter,
    });
  });

  const getFieldDisplay = (field: (typeof fields)[number]) => {
    if (field.minSize !== null && field.maxSize !== null) {
      if (field.minSize === field.maxSize) {
        return (
          <span className="text-theme-on-background-variant text-xs font-medium">
            Præcis{" "}
            <span className="text-theme-on-background font-bold">
              {field.minSize}
            </span>{" "}
            {pluralize(field.minSize, "kontaktperson", "kontaktpersoner")}
          </span>
        );
      }
      return (
        <span className="text-theme-on-background-variant text-xs font-medium">
          Mellem{" "}
          <span className="text-theme-on-background font-bold">
            {field.minSize}
          </span>
          og{" "}
          <span className="text-theme-on-background font-bold">
            {field.maxSize}
          </span>{" "}
          {pluralize(field.maxSize, "kontaktperson", "kontaktpersoner")}
        </span>
      );
    }
    if (field.minSize !== null) {
      return (
        <span className="text-theme-on-background-variant text-xs font-medium">
          Mindst{" "}
          <span className="text-theme-on-background font-bold">
            {field.minSize}
          </span>{" "}
          {pluralize(field.minSize, "kontaktperson", "kontaktpersoner")}
        </span>
      );
    }
    if (field.maxSize !== null) {
      return (
        <span className="text-theme-on-background-variant text-xs font-medium">
          Højst{" "}
          <span className="text-theme-on-background font-bold">
            {field.maxSize}
          </span>{" "}
          {pluralize(field.maxSize, "kontaktperson", "kontaktpersoner")}
        </span>
      );
    }
  };

  return (
    <div
      className={twMerge(
        "flex flex-col items-center justify-center gap-y-6",
        hasFields && "gap-y-10",
      )}
    >
      <form id={formId} onSubmit={onSubmit} />
      {!hasFields && <IconCircle icon="user" />}
      <div className="flex max-w-96 flex-col items-center justify-center gap-y-4">
        <h2 className="text-base font-medium">{title}</h2>
        <p className="text-theme-on-background-variant text-center text-xs font-medium">
          {description}
        </p>
      </div>
      <div className="flex w-xl flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          {hasFields && (
            <div className="text-sm font-medium">Valgte afdelinger</div>
          )}
          <DialogTrigger
            isOpen={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) setEditingIndex(null);
            }}
          >
            <Button
              className={twMerge("min-w-16", hasFields ? "" : "mx-auto")}
              intent={hasFields ? "secondary" : "primary"}
              onPress={() => setEditingIndex(null)}
            >
              {hasFields ? (
                <div className="flex w-full items-center justify-between gap-2.5">
                  <Plus className="text-theme-on-background -ml-2 size-6" />{" "}
                  <span>Tilføj</span>
                </div>
              ) : (
                "Tilføj afdeling"
              )}
            </Button>
            {isDialogOpen && (
              <AddDepartmentModal
                departments={departments}
                initialValues={modalInitialValues}
                isEditing={editingIndex !== null}
                reset={() => {
                  setIsDialogOpen(false);
                  setEditingIndex(null);
                }}
                proceed={(data) => {
                  if (editingIndex !== null) {
                    // Updating existing field
                    updateField(editingIndex, data);
                  } else if (
                    fields.some((field) => field.department === data.department)
                  ) {
                    // If the department already exists, just update it
                    const index = fields.findIndex(
                      (field) => field.department === data.department,
                    );
                    updateField(index, data);
                  } else {
                    append(data);
                  }
                  setIsDialogOpen(false);
                  setEditingIndex(null);
                }}
              />
            )}
          </DialogTrigger>
        </div>
        <div>
          {fields.map((field, index) => (
            <RACButton
              key={field.id}
              className="border-theme-outline-variant hover:bg-theme-on-background-hover mb-2 flex w-full items-center justify-between gap-4 rounded border p-4 hover:cursor-pointer"
              onPress={() => {
                setEditingIndex(index);
                setIsDialogOpen(true);
              }}
            >
              <div className="flex shrink grow-0 items-center gap-1 truncate *:text-nowrap">
                {getFieldDisplay(field)}
                <span className="text-theme-on-background-variant text-xs font-medium">
                  {" "}
                  i{" "}
                </span>
                <div className="truncate text-xs font-bold">
                  {field.department}
                </div>
              </div>
              <Button
                intent="tertiary"
                size="rounded"
                className="min-w-0 shrink-0"
                onPress={() => {
                  if (fields.length === 1) {
                    update({
                      value: undefined,
                    });
                    return;
                  }
                  remove(fields.indexOf(field));
                }}
              >
                <X className="text-theme-on-background-variant size-4" />
              </Button>
            </RACButton>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddDepartmentModal({
  departments,
  proceed,
  reset,
  initialValues,
  isEditing,
}: {
  departments: string[];
  proceed?: (data: z.output<typeof departmentFormSchema>) => void;
  reset?: () => void;
  initialValues?: z.infer<typeof departmentFormSchema>;
  isEditing?: boolean;
}) {
  const {
    control,
    reset: resetForm,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(departmentFormSchema),
    defaultValues: initialValues ?? {
      department: "",
      minSize: null,
      maxSize: null,
    },
  });

  useEffect(() => {
    console.debug("Errors", errors);
  }, [errors]);

  const onSubmit = handleSubmit((data) => {
    console.debug("Add Department Modal Submit Data:", data);
    proceed?.(data);
  });

  return (
    <Modal size="xl" padding="lg">
      <div className="text-theme-on-background flex flex-col items-center justify-center gap-y-6">
        <IconCircle icon="user-cog" />
        <div className="flex flex-col items-center justify-center">
          <h2 className="text-base font-medium">
            {isEditing ? "Rediger afdeling" : "Tilføj afdeling"}
          </h2>
          <p className="text-theme-on-background-variant mt-4 text-center text-sm font-medium">
            Vælg en afdeling samt hvor mange kontaktpersoner der skal være i
            den, for at den matcher din målgruppe.
          </p>
        </div>
        {/* Add form elements for adding a department here */}
        <form onSubmit={onSubmit} className="flex w-full flex-col gap-y-4">
          <Controller
            name="department"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                selectedKey={field.value}
                onSelectionChange={field.onChange}
                errorMessage={fieldState.error?.message}
                aria-label="Vælg afdeling"
                placeholder="Vælg afdeling"
              >
                {departments.map((department) => (
                  <SelectItem id={department} key={department}>
                    {department}
                  </SelectItem>
                ))}
              </Select>
            )}
          />
          <div className="flex gap-4">
            <NumberField
              control={control}
              name="minSize"
              aria-label="Minimum antal kontaktpersoner i afdelingen"
              className="basis-1/2"
              placeholder="Min."
            ></NumberField>
            <NumberField
              control={control}
              name="maxSize"
              aria-label="Maksmium antal kontaktpersoner i afdelingen"
              className="basis-1/2"
              placeholder="Max."
            ></NumberField>
          </div>
          <div className="flex justify-center gap-4">
            <Button
              intent="secondary"
              className="min-w-28"
              onPress={() => {
                resetForm();
                reset?.();
              }}
            >
              Annuller
            </Button>
            <Button type="submit" className="min-w-28">
              Gem
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
